import React from 'react';
import FormErrorMessage from './form-error-message';
import FormLoadingMessage from './form-loading-message';
import FormSuccessMessage from './form-success-message';

interface FormStatusWrapperProps {
  success: boolean;
  successMessage?: string;
  error: boolean;
  errorMessage?: string;
  loading: boolean;
  loadingMessage?: string;
  children: React.ReactNode;
  alwaysDisplayChildren?: boolean;
}

/**
 * FormStatusWrapper is a component that wraps a form and displays a loading, error, or success message
 * If none of those are true, it will display the children.
 * Loading takes priority over error, and error takes priority over success.
 * If alert messages are not provided, default messages will be used.
 * If alwaysDisplayChildren is true, the children will always be displayed, even if there is a loading, error, or success message.
 * @param props
 * @returns
 */
export default function FormStatusWrapper(props: FormStatusWrapperProps) {
  const {
    success,
    successMessage,
    error,
    errorMessage,
    loading,
    loadingMessage,
    children,
    alwaysDisplayChildren,
  } = props;

  if (loading) {
    if (alwaysDisplayChildren) {
      return (
        <>
          <FormLoadingMessage
            message={loadingMessage || 'Loading...'}
            data-testid="loading-message"
          />
          {children}
        </>
      );
    }
    return <FormLoadingMessage message={loadingMessage || 'Loading...'} />;
  }
  if (error) {
    if (alwaysDisplayChildren) {
      return (
        <>
          <FormErrorMessage
            message={errorMessage || 'An Error Occurred'}
            data-testid="error-message"
          />
          {children}
        </>
      );
    }
    return <FormErrorMessage message={errorMessage || 'An Error Occurred'} />;
  }
  if (success) {
    if (alwaysDisplayChildren) {
      return (
        <>
          <FormSuccessMessage
            message={successMessage || 'Successful'}
            data-testid="success-message"
          />
          {children}
        </>
      );
    }
    return <FormSuccessMessage message={successMessage || 'Successful'} />;
  }
  if (!children) {
    throw new Error('FormStatusWrapper must have children');
  }
  // React moans without fragment
  return <>{children}</>;
}

FormStatusWrapper.defaultProps = {
  successMessage: undefined,
  errorMessage: undefined,
  loadingMessage: undefined,
  alwaysDisplayChildren: false,
};
